<script>
import { cloneDeep } from 'lodash-es';
import ValidatorMixin from '../../mixins/validatorMixin';
import ItineraryDayProgramListExpandedRow from './ItineraryDayProgramListExpandedRow';
import ItineraryDayProgramImageModal from './ItineraryDayProgramImageModal';

export default {
  name: 'ItineraryDayProgramList',
  mixins: [ValidatorMixin],
  components: {
    ItineraryDayProgramListExpandedRow,
    ItineraryDayProgramImageModal,
  },
  props: {
    itineraryId: {
      type: Number,
      required: true,
    },
    dayProgramLoading: {
      type: Boolean,
      default: false,
    },
    dayProgramItems: {
      type: Array,
      default: () => [],
    },
    dayProgramError: {
      type: Boolean,
      default: false,
    },
    selectedDomain: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dayProgramEntries: [],
      dayProgramDomainEntries: [],
      headers: [
        { text: this.$t('itinerary.edit.dayProgram.table.days'), sortable: false },
        { text: this.$t('itinerary.edit.dayProgram.table.description'), sortable: false },
        { text: this.$t('itinerary.edit.dayProgram.table.dayText'), sortable: false },
        { text: this.$t('itinerary.edit.dayProgram.table.location'), sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.hotels'), sortable: false, align: 'center' },
        { text: 'Extras', sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.media'), sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.mealPlan'), sortable: false, align: 'center' },
        { text: this.$t('itinerary.edit.dayProgram.table.actions'), sortable: false, align: 'center' },
      ],
      toggled: [],
      valid: true,
    };
  },
  created() {
    this.dayProgramEntries = cloneDeep(this.dayProgramItems);
    this.toggled = new Array(this.dayProgramItems.length).fill(false);
    if (this.selectedDomain) {
      this.dayProgramDomainEntries = cloneDeep(this.selectedDomain.dayPrograms);
    }
  },
  methods: {
    toggle(index) {
      this.toggled[index] = !this.toggled[index];
      this.toggled = [...this.toggled];
    },
    updateEntry(event) {
      this.dayProgramEntries[event.index][event.model] = event.value;
    },
    updateDomainEntry(event) {
      const dayProgramEntry = this.dayProgramEntries[event.index];
      const dayProgramDomainIndex = this.dayProgramDomainEntries
        .map(dayProgram => dayProgram.itineraryDayProgramId)
        .indexOf(dayProgramEntry.id);
      this.dayProgramDomainEntries[dayProgramDomainIndex][event.model] = event.value;
    },
    getDayProgramDomain(dayProgramId) {
      let dayProgramDomain = this.dayProgramDomainEntries
        .filter(dayProgram => dayProgram.itineraryDayProgramId === dayProgramId)[0]
        || null;
      if (!dayProgramDomain) {
        dayProgramDomain = {
          itineraryDomainId: this.selectedDomain.id,
          itineraryDayProgramId: dayProgramId,
          description: null,
          teaserText: null,
          text: null,
        };
        this.dayProgramDomainEntries.push(dayProgramDomain);
      }
      return dayProgramDomain;
    },
    save() {
      this.$emit('saveDayProgram', this.dayProgramEntries);
      if (this.selectedDomain) {
        this.$emit('updateDomain', {
          ...this.selectedDomain,
          dayPrograms: this.dayProgramDomainEntries,
        });
      }
    },
    addDayProgram() {
      this.$emit('addDayProgramEntry', this.itineraryId);
      this.save();
    },
    getExtraCount(day) {
      return day.extras.filter(extra =>
        extra.type === 'optional' || extra.type === 'included').length;
    },
  },
  watch: {
    dayProgramItems() {
      this.dayProgramEntries = cloneDeep(this.dayProgramItems);
      this.toggled = new Array(this.dayProgramItems.length).fill(false);
    },
    selectedDomain(val) {
      if (val) {
        this.dayProgramDomainEntries = cloneDeep(this.selectedDomain.dayPrograms);
      } else {
        this.dayProgramDomainEntries = [];
      }
    },
  },
};
</script>

<template>
  <v-container class="pa-0 fluid">
    <v-layout row wrap>
      <v-flex class="my-4">
        <v-form v-model="valid">
          <v-data-table
            :items="dayProgramEntries"
            :headers="headers"
            :loading="dayProgramLoading"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ $t('itinerary.edit.dayProgram.dayIterator', [props.index + 1]) }}</td>
                <td>
                  <v-text-field
                    v-model="props.item.description"
                    :label="$t('itinerary.edit.dayProgram.table.description')"
                    :rules="[
                      rules.requiredWith.bind(this, props.item.countryId),
                    ]"
                    hide-details
                    class="my-3"
                  />
                  <v-text-field
                    v-if="selectedDomain"
                    :value="getDayProgramDomain(props.item.id).description"
                    :label="$t('itinerary.edit.dayProgram.table.localDescription')"
                    hide-details
                    class="my-3"
                    @input="updateDomainEntry(
                      {model: 'description', value: $event, index: props.index}
                    )"
                  />
                </td>
                <td>
                  <template>
                    <v-btn
                      fab
                      small
                      depressed
                      :color="props.item.teaserText && props.item.text ? 'success' : 'default'"
                      @click="toggle(props.index)"
                    >
                      <v-icon>book</v-icon>
                    </v-btn>
                  </template>
                </td>
                <td class="text-xs-center">
                  <template v-if="props.item.countryId">
                    <v-btn
                      small
                      depressed
                      color="primary"
                      @click="$emit('openLocationModal', props.item)"
                    >
                      {{ props.item.country }} - {{ props.item.area }}
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      :disabled="!props.item.description"
                      fab
                      small
                      depressed
                      color="primary"
                      @click="$emit('openLocationModal', props.item)"
                    >
                      <v-icon>map</v-icon>
                    </v-btn>
                  </template>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    :disabled="!props.item.countryId"
                    fab
                    small
                    depressed
                    :color="props.item.hotelCount ? 'primary' : 'warning'"
                    @click="$emit('openHotelModal', props.item)"
                  >
                    <template v-if="props.item.hotelCount">
                      {{ props.item.hotelCount }}
                    </template>
                    <template v-else>
                      <v-icon>hotel</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    fab
                    small
                    depressed
                    :disabled="!props.item.countryId"
                    :color="getExtraCount(props.item) ? 'primary' : 'warning'"
                    @click="$emit('openExcursionModal', props.item)"
                  >
                    <template v-if="getExtraCount(props.item)">
                      {{ getExtraCount(props.item) }}
                    </template>
                    <template v-else>
                      <v-icon>directions_walk</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    fab
                    small
                    depressed
                    :color="props.item.imageCount ? 'primary' : 'warning'"
                    @click="$emit('openImageModal', props.item)"
                  >
                    <template v-if="props.item.imageCount">
                      {{ props.item.imageCount }}
                    </template>
                    <template v-else>
                      <v-icon>collections</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    fab
                    small
                    depressed
                    :color="props.item.mealplanCount ? 'primary' : 'warning'"
                    @click="$emit('openMealPlanModal', props.item)"
                  >
                    <template v-if="props.item.mealplanCount">
                      {{ props.item.mealplanCount }}
                    </template>
                    <template v-else>
                      <v-icon>fastfood</v-icon>
                    </template>
                  </v-btn>
                </td>
                <td class="text-xs-center">
                  <v-btn
                    fab
                    small
                    depressed
                    color="error"
                    @click="$emit('deleteDayProgramEntry', {
                      itineraryId,
                      dayProgramId: props.item.id
                    })"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
              <div v-if="toggled[props.index]" class="expanded-row">
                <div class="expanded-row-data">
                  <itinerary-day-program-list-expanded-row
                    v-if="selectedDomain"
                    :title="$t('itinerary.edit.dayProgram.localText')"
                    :item="getDayProgramDomain(props.item.id)"
                    :index="props.index"
                    class="expanded-row-form"
                    @input="updateDomainEntry"
                  />
                  <itinerary-day-program-list-expanded-row
                    :title="$t('itinerary.edit.dayProgram.supplierText')"
                    :item="props.item"
                    :index="props.index"
                    class="expanded-row-form"
                    @input="updateEntry"
                  />
                </div>
              </div>
            </template>
          </v-data-table>
          <v-btn
            :loading="dayProgramLoading"
            color="success"
            class="right mr-4 mt-4"
            @click="addDayProgram"
          >
            {{ $t('buttons.add') }}
            <v-icon class="ml-1">add</v-icon>
          </v-btn>
          <v-btn
            :loading="dayProgramLoading"
            color="primary"
            class="right mr-4 mt-4"
            @click="save"
          >
            {{ $t('buttons.save') }}
            <v-icon class="ml-1">save</v-icon>
          </v-btn>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" scoped>
  @import '../../assets/scss/variables.scss';

  .expanded-row {
    position: relative;
    display: table-row;
    height: $expanded-row-height;
    width: 100%;
    overflow: hidden;
  }

  .expanded-row-input {
    margin-top: $margin-lg;
    margin-left: $margin-lg;
    width: $expanded-row-input-width;
  }

  .expanded-row-data {
    position: absolute;
    width: 100%;
    height: $expanded-row-height;
    overflow: auto;
  }

  .expanded-row-form {
    width: 50%;
    float: right;
  }
</style>



// WEBPACK FOOTER //
// src/components/itinerary/ItineraryDayProgramList.vue